// A vertical-spine timeline where the rail itself IS the temporal distance:
// one continuous dashed rail runs behind every row, and the *height* of the
// gap between rows is proportional (log-scaled, tightly capped) to elapsed
// time. Sparse measurement / milestone timelines get a `scale='days'` tuning;
// dense event feeds use the default `'minutes'`.

import { useMemo, type ReactNode } from 'react';
import { Icon } from '../Icon.tsx';
import { fmtDur } from '../../lib/format.ts';

export type SpineItem = {
  key: string;
  at: number; // unix seconds — event start
  /** Optional event end (unix seconds). When provided AND > at, the rail
   *  segment between this item and the next-newer item renders the duration
   *  as a solid colored bar, leaving only the awake-gap dashed. Point-in-time
   *  events (a diaper, a measurement) omit this. */
  endAt?: number;
  render: () => ReactNode;
  /** Optional accent colour (CSS var) — used for both the dot AND the
   *  duration rail segment. Defaults to text-3. */
  accent?: string;
};

/** `minutes` — high-cadence event feeds (legacy, compressed).
 *  `day`     — single day's events with duration spans (most generous).
 *  `days`    — sparse growth / milestone timelines. */
export type TimelineScale = 'minutes' | 'day' | 'days';

type Props = {
  items: SpineItem[]; // any order; sorted desc by `at` internally
  scale?: TimelineScale;
};

const SCALE_TUNING: Record<
  TimelineScale,
  {
    minPx: number;
    maxPx: number;
    refSec: number;
    slope: number;
    labelFloorSec: number;
    fmt: (s: number) => string;
  }
> = {
  minutes: {
    minPx: 14,
    maxPx: 96,
    refSec: 60 * 60,
    slope: 14,
    labelFloorSec: 60 * 60,
    fmt: (s) => {
      if (s < 90) return `${Math.max(1, Math.round(s))}s`;
      if (s < 60 * 60) return `${Math.round(s / 60)}m`;
      return fmtDur(Math.round(s / 60));
    },
  },
  day: {
    minPx: 22,
    maxPx: 220,
    refSec: 900, // 15 min reference — a 2h gap reads clearly tall
    slope: 26,
    labelFloorSec: 15 * 60,
    fmt: (s) => {
      if (s < 90) return `${Math.max(1, Math.round(s))}s`;
      if (s < 60 * 60) return `${Math.round(s / 60)}m`;
      return fmtDur(Math.round(s / 60));
    },
  },
  days: {
    minPx: 14,
    maxPx: 72,
    refSec: 86400,
    slope: 14,
    labelFloorSec: 86400,
    fmt: (s) => {
      const days = Math.round(s / 86400);
      if (days < 1) return '';
      if (days < 14) return `${days}d`;
      const weeks = Math.round(days / 7);
      if (weeks < 9) return `${weeks}w`;
      const months = Math.round(days / 30.44);
      if (months < 18) return `${months}mo`;
      const years = Math.round((days / 365.25) * 10) / 10;
      return `${years}y`;
    },
  },
};

const RAIL_LEFT = 10; // x of the rail inside the spine container

export function TimelineSpine({ items, scale = 'minutes' }: Props) {
  const t = SCALE_TUNING[scale];

  const sorted = useMemo(() => [...items].sort((a, b) => b.at - a.at), [items]);

  const gapPx = (seconds: number): number => {
    if (seconds <= 0) return t.minPx;
    const v = Math.max(0, Math.log(seconds / t.refSec));
    return Math.min(t.maxPx, t.minPx + t.slope * v);
  };

  if (!sorted.length) return null;

  return (
    <div style={{ position: 'relative', paddingLeft: 26 }}>
      {/* One continuous dashed rail behind everything. SVG so the dash
          renders identically on every renderer, and we can lay it out as
          a single element instead of stitching per-gap pieces. */}
      <svg
        aria-hidden
        width="2"
        height="100%"
        style={{
          position: 'absolute',
          left: RAIL_LEFT - 1,
          top: 8,
          bottom: 8,
          height: 'calc(100% - 16px)',
          overflow: 'visible',
          pointerEvents: 'none',
        }}
        preserveAspectRatio="none"
      >
        <line
          x1="1"
          y1="0"
          x2="1"
          y2="100%"
          stroke="var(--text-3)"
          strokeWidth="1.5"
          strokeDasharray="2 4"
          strokeLinecap="round"
          opacity="0.6"
        />
      </svg>

      {sorted.map((it, i) => {
        const next = sorted[i + 1];
        const gap = next ? Math.max(0, it.at - next.at) : 0;
        // Sorted desc, so `next` is the OLDER event. Its duration grows
        // forward in time = UPWARD on screen, occupying the bottom of the
        // gap above its row. The remainder (top of gap) is awake-time.
        const nextEnd = next?.endAt ?? next?.at ?? 0;
        const nextDurSec = next ? Math.max(0, Math.min(gap, nextEnd - next.at)) : 0;
        const awakeSec = next ? Math.max(0, gap - nextDurSec) : 0;
        // When the older event has duration we render it on a LINEAR
        // px/minute scale so 2h reads visibly longer than 49m — the legacy
        // log scale squashes them together. The awake gap above gets its
        // own much-tighter scale (long awake windows shouldn't dominate
        // the timeline).
        let segHeight = 0;
        let gapHeight = 0;
        if (next && nextDurSec > 0) {
          const durMin = nextDurSec / 60;
          const awakeMin = awakeSec / 60;
          segHeight = Math.round(Math.min(220, Math.max(10, durMin * 1.2)));
          const awakeHeight = Math.round(
            awakeMin <= 0 ? 0 : Math.min(70, Math.max(8, awakeMin * 0.7)),
          );
          gapHeight = segHeight + awakeHeight;
        } else if (next) {
          gapHeight = Math.round(gapPx(gap));
        }
        return (
          <div key={it.key}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
              <span
                style={{
                  position: 'absolute',
                  left: RAIL_LEFT - 5 - 26, // align dot centre on rail (- paddingLeft)
                  top: 12,
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: it.accent ?? 'var(--text-2)',
                  boxShadow: '0 0 0 3px var(--bg-app)',
                  zIndex: 2,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>{it.render()}</div>
            </div>
            {next && (
              <div
                style={{
                  height: gapHeight,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'visible',
                }}
              >
                {segHeight > 2 && (
                  <>
                    {/* Duration bar — extends DOWN past the gap edge by 17px
                        so its bottom meets the next event's start-dot centre,
                        making the dot read as the START marker on the bar. */}
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: RAIL_LEFT - 26 - 3, // centred on the rail
                        bottom: -17,
                        width: 7,
                        height: segHeight + 17,
                        background: next.accent ?? 'var(--text-3)',
                        opacity: 0.55,
                        borderRadius: '4px 4px 0 0',
                        zIndex: 0,
                      }}
                    />
                    {/* End cap — small filled dot at the top of the bar marks
                        the event's END time. Matches the start-dot visual so
                        the span reads as start → bar → end. */}
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: RAIL_LEFT - 26 - 4,
                        bottom: segHeight - 4,
                        width: 9,
                        height: 9,
                        borderRadius: 999,
                        background: next.accent ?? 'var(--text-3)',
                        boxShadow: '0 0 0 3px var(--bg-app)',
                        zIndex: 2,
                      }}
                    />
                  </>
                )}
                {awakeSec >= t.labelFloorSec && gapHeight - segHeight >= 22 && (
                  <span
                    style={{
                      position: 'absolute',
                      left: RAIL_LEFT - 26 + 10, // sit just right of the rail
                      top: `${Math.max(8, (gapHeight - segHeight) / 2)}px`,
                      fontSize: 10,
                      color: 'var(--text-3)',
                      letterSpacing: 0.04,
                      background: 'var(--bg-app)',
                      padding: '1px 5px',
                      borderRadius: 6,
                      lineHeight: 1.2,
                    }}
                  >
                    {t.fmt(awakeSec)}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Shared row template for measurement / milestone timelines: optional
 *  leading glyph, title with optional notes-icon, subtitle line, click
 *  handler. Was a near-duplicate component on /growth and /milestones. */
export function TimelineEntry({
  leading,
  title,
  subtitle,
  hasNotes,
  onClick,
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  hasNotes?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 4px',
        cursor: 'pointer',
      }}
    >
      {leading != null && <span style={{ flexShrink: 0 }}>{leading}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            color: 'var(--text-1)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
          {hasNotes && (
            <span style={{ color: 'var(--text-3)' }} title="Has a note">
              <Icon.edit s={11} />
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
          {subtitle}
        </div>
      </div>
    </button>
  );
}
